/**
 * MarketingMetricsPage — Phase 32.9.
 *
 * Views / clicks / conversions from `marketing_metrics`. The API
 * endpoint for metrics is not yet exposed (metrics are written by a
 * future webhook from ATLAS). This page shows the approved campaigns
 * as a ready-state, with placeholder metric values until the endpoint
 * lands in a follow-up phase.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

interface CampaignSummary {
  id: string;
  product_id: string;
  status: string;
  created_at: number;
  slug: string | null;
  title_tag: string | null;
}

function fetchCampaigns(): Promise<{ campaigns: CampaignSummary[]; total: number }> {
  return apiFetch<{ campaigns: CampaignSummary[]; total: number }>('/admin/marketing/campaigns');
}

function MetricCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 16px' }}>
      <div style={{ fontSize: '14pt', fontWeight: 700, color: 'var(--omega-ink)' }}>{value}</div>
      <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export function MarketingMetricsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketing', 'campaigns'],
    queryFn: fetchCampaigns,
    refetchInterval: 60_000,
  });

  const published = (data?.campaigns ?? []).filter(
    c => c.status === 'approved' || c.status === 'published',
  );

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Metrics' },
        ]}
      />

      <h1 style={{ fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', margin: '8px 0 20px' }}>
        Campaign Metrics
      </h1>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load data.</div>}

      {!isPending && !isError && published.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No live campaigns yet.{' '}
          <Link to="/omega/marketing/campaigns" style={{ color: '#7dd3fc' }}>
            Approve a campaign
          </Link>{' '}
          to start tracking metrics.
        </div>
      )}

      {published.map(c => (
        <div
          key={c.id}
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 6,
            marginBottom: 12,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link
              to={`/omega/marketing/campaigns/${c.id}`}
              style={{ fontSize: '10pt', fontWeight: 700, color: 'var(--omega-ink)', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {c.title_tag ?? c.slug ?? c.product_id}
            </Link>
            {c.slug && (
              <span style={{ fontSize: '8pt', color: '#7dd3fc', flexShrink: 0 }}>/{c.slug}</span>
            )}
          </div>

          {/* Metric row — placeholder values until API endpoint lands */}
          <div style={{ display: 'flex', justifyContent: 'space-around', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <MetricCell label="Views" value="—" />
            <MetricCell label="Clicks" value="—" />
            <MetricCell label="Conversions" value="—" />
          </div>

          <div style={{ padding: '8px 14px', fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.04em' }}>
            Live metric ingestion wires in Phase 32.10 (ATLAS webhook → marketing_metrics store).
          </div>
        </div>
      ))}
    </div>
  );
}
